import React, {
    useCallback,
    useState,
    useRef,
    SyntheticEvent,
    useEffect,
} from 'react';
import { useQuery } from '@apollo/client';
import cx from 'classnames';
import { useHistory } from 'react-router-dom';

import {
    Button,
    FileDropZone,
    IconInfoCircle,
    IconFile,
    IconUploadCloud,
    IconX,
    LoadingSpinner,
    TabPanel,
    Tooltip,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import {
    constants,
    locale,
    getFileInputAccept,
    getFileSize,
    getSupportedProsLabel,
} from '@trqk/utils';

import {
    ActiveIngestions,
    FailedIngestionsTable,
    ManageStatementsTable,
} from './components';
import {
    ConfirmStatementDeletion,
    StatementTooLarge,
    MaxStatementBatchExceeded,
    PlanExpired,
} from '../../toastMessages';
import {
    useEntities,
    useTasks,
    useToast,
    useUploadStatements,
    usePaymentPlanStatus,
} from '../../../hooks';
import {
    headerHeight,
    modalFooterHeight,
    modalWidth,
    modalFooterShimHeight,
    tableHeight,
} from './constants';
import { HowIngestionWorks, PROStatementHelp } from '../../tooltips';
import { HASHES } from '../../../constants';
import { getFeatureFlags } from '../../../featureFlags';

const { GET_SESSION_CURRENT_USER, GET_STATEMENTS_UI_STATE } = queryStrings;
const { MAX_STATEMENT_SIZE_MB, MAX_STATEMENT_UPLOAD_BATCH_SIZE } = constants;

const supportedProsLabel = getSupportedProsLabel('and');

const DropZoneContent = (): JSX.Element => (
    <div className="flex flex-col items-center">
        <IconUploadCloud fillColor="white" width={80} />
        <div className="font-bold text-white font-lg">
            {locale.enUS.MANAGE_STATEMENTS.DROP_TO_UPLOAD}
        </div>
    </div>
);

const fileInputAccept = getFileInputAccept(['csv']);
const tabPanelHeight = tableHeight + headerHeight;

export const ManageStatements = (
    props: ModalContentProps<Record<string, never>>,
): JSX.Element => {
    const { limitStatementUploadBatchSize, limitStatementUploadFileSize } =
        getFeatureFlags();
    const history = useHistory<{
        createGapAnalysis: {
            statementPeriodLabel: string;
            performancePeriod: SparseDateRange;
            pro: Maybe<string>;
        };
    }>();
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const { sendTaskEvent } = useTasks();
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const statementsUiStateQuery = useQuery(GET_STATEMENTS_UI_STATE);
    const unviewedStatements: string[] =
        statementsUiStateQuery?.data?.uiState?.statements?.unviewedStatements ??
        [];
    const userId = currentUserQuery?.data?.session?.currentUser?.id;

    const { failedIngestions, handleFiles, uploadTasks } =
        useUploadStatements();
    const { activeEntities, loading } = useEntities();
    const { subscriptionSettings } = usePaymentPlanStatus();
    const subscriptionExpirationDate =
        subscriptionSettings?.subscription_expiration_date ?? null;
    const { add: addToast } = useToast();
    const [selectedStatementIds, setSelectedStatementIds] = useState<string[]>(
        [],
    );

    // A timestamp that will be used to reset row selection in the table when statements are deleted
    const [resetRowSelection, setResetRowSelection] =
        useState<Maybe<number>>(null);

    const deleteStatementErrorMessage = `${
        locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_FAILURE.PART_1
    } ${
        selectedStatementIds.length === 1
            ? locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_FAILURE
                  .STATEMENT
            : locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_FAILURE
                  .STATEMENTS
    }`;

    const deleteStatementSuccessMessage = `${
        locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_SUCCESS.PART_1
    } ${
        selectedStatementIds.length === 1
            ? locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_SUCCESS
                  .STATEMENT_WAS
            : locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_SUCCESS
                  .STATEMENTS_WERE
    } ${
        locale.enUS.MANAGE_STATEMENTS.TOASTS.STATEMENT_DELETION_SUCCESS.PART_2
    }`;

    const completeStatementIds = activeEntities
        .map((activeEntity: ActiveEntity): string[] =>
            activeEntity.completedStatementViews.reduce(
                (acc: string[], view: TrqkStatementView): string[] => {
                    if (view.statement_id) {
                        acc.push(view.statement_id);
                    }

                    return acc;
                },
                [] as string[],
            ),
        )
        .flat();

    // Prevent progress bars from showing up if the ingestion for this statement is either complete
    // or has an error
    const inProgressTasks = uploadTasks.filter(
        (task: UploadStatementTask): boolean => {
            if (
                task.error !== null ||
                task.done ||
                completeStatementIds.some(
                    (statementId: string): boolean =>
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        statementId === task?.output?.statement_id ??
                        'unknownId',
                )
            ) {
                return false;
            }

            return true;
        },
    );

    const addFiles = useCallback((): void => {
        const hasExpiredSubscription = subscriptionExpirationDate
            ? new Date() > new Date(subscriptionExpirationDate)
            : false;

        if (hasExpiredSubscription) {
            addToast({
                kind: 'warning-card',
                RenderContent: PlanExpired,
                renderContentInput: {
                    fileType: 'statements',
                    onNavigate: () => {
                        props.remove({
                            canceled: true,
                            success: false,
                        });
                    },
                },
                persist: true,
            });
        } else if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }, [subscriptionExpirationDate]);

    const [activeTabKey, setActiveTabKeyPrivate] = useState(
        history.location.hash ?? HASHES.MANAGE_STATEMENTS.COMPLETE,
    );

    const setActiveTabKey = (nextActiveTabKey: string) => {
        setActiveTabKeyPrivate(nextActiveTabKey);

        history.push({
            hash: nextActiveTabKey,
            search: history.location.search,
        });
    };

    const setSelectedFailedIngestionRowIds = useState<Record<string, boolean>>(
        {},
    )[1];

    /**
     * Check for:
     *  - user is trying to upload any files that are too large (this is controlled by a feature
     * flag called limitStatementUploadFileSize)
     * - user is trying to upload too many files at once (this is controlled by a feature flag
     * called limitStatementUploadBatchSize)
     *
     * If either is true, show a warning toast and do not proceed. Otherwise, proceed with upload.
     * @param {File[]} files the files that were selected for upload
     */
    const uploadOrRejectFiles = useCallback(
        (files: File[]): void => {
            const tooManyFiles = limitStatementUploadBatchSize
                ? files.length + inProgressTasks.length >
                  MAX_STATEMENT_UPLOAD_BATCH_SIZE
                : false;

            const someFileIsTooBig = limitStatementUploadFileSize
                ? files.some(
                      (file: File): boolean =>
                          getFileSize({ bytes: file.size, unit: 'MB' }) >
                          MAX_STATEMENT_SIZE_MB,
                  )
                : false;

            if (tooManyFiles) {
                addToast({
                    kind: 'warning-card',
                    RenderContent: MaxStatementBatchExceeded,
                    renderContentInput: {
                        currentUploads: inProgressTasks.length,
                    },
                });
            } else if (someFileIsTooBig) {
                addToast({
                    kind: 'warning-card',
                    RenderContent: StatementTooLarge,
                });
            } else {
                handleFiles(files);
                setActiveTabKey(HASHES.MANAGE_STATEMENTS.UPLOADS);
            }
        },
        [addToast, handleFiles, inProgressTasks],
    );

    const completeCount = activeEntities.reduce(
        (acc: number, { completedStatementViews }: ActiveEntity): number =>
            acc + completedStatementViews.length,
        0,
    );

    const statementPeriodLabel =
        history.location.state?.createGapAnalysis?.statementPeriodLabel ?? null;
    const performancePeriod =
        history.location.state?.createGapAnalysis?.performancePeriod ?? null;
    const pro = history.location.state?.createGapAnalysis?.pro ?? null;

    // This data is used to display a <ManageGapAnalysisHeader> within the Uploads tab when the user
    // has come to the Manage Statements modal after selecting a performance period for a Gap
    // Analysis report that is missing a statement. The user is prompted to upload a specific
    // statement in the <ManageGapAnalysisHeader> content.
    const [manageGapAnalysisHeaderData, setManageGapAnalysisHeaderData] =
        useState<ManageGapAnalysisHeaderData | null>(null);

    useEffect(() => {
        // history.location.state will drop the createGapAnalysis data after uploading a file with
        // the browser's file picker, so we need to save these details in a state variable
        if (statementPeriodLabel !== null && performancePeriod !== null) {
            setManageGapAnalysisHeaderData({
                statementPeriodLabel,
                performancePeriod,
                pro,
            });
        }
    }, [statementPeriodLabel, performancePeriod, pro]);

    const handleDrop = useCallback(
        ({ validFiles, invalidFiles }: HandleDropInput) => {
            const hasExpiredSubscription = subscriptionExpirationDate
                ? new Date() > new Date(subscriptionExpirationDate)
                : false;

            if (hasExpiredSubscription) {
                addToast({
                    kind: 'warning-card',
                    RenderContent: PlanExpired,
                    renderContentInput: {
                        fileType: 'statements',
                        onNavigate: () => {
                            props.remove({
                                canceled: true,
                                success: false,
                            });
                        },
                    },
                    persist: true,
                });
            } else if (invalidFiles.length > 0) {
                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.DROPPED_WRONG_FILE_TYPE.STATEMENTS,
                });
            } else {
                uploadOrRejectFiles(validFiles);
            }
        },
        [subscriptionExpirationDate],
    );

    return (
        <FileDropZone
            addClasses="rounded-lg"
            acceptFileTypes={['csv']}
            DropZoneContent={DropZoneContent}
            handleDrop={handleDrop}
        >
            <div className="bg-white rounded-lg" style={{ width: modalWidth }}>
                <div className="px-10 pt-4 pb-4 border-l-4 rounded-tl-lg border-secondary-500">
                    <div className="flex justify-end">
                        <button
                            className="mt-4 mb-2 respond-to-mouse"
                            type="button"
                            onClick={(): void => {
                                props.remove({
                                    canceled: true,
                                    success: false,
                                });

                                // When closing the modal, cancel tasks that have paused due to an error
                                // but have not yet generated a statementView. This will prevent duplicate
                                // progress bars for failed ingestions that did not time out. This might
                                // get confusing because explicitly failed ingestions will disappear when
                                // the modal is closed, whereas timeouts will still show up - but for
                                // failures that did not create a statementView, we have no unique
                                // identifier to deduplicate across FailureRows and ProgressRows.
                                uploadTasks.forEach(
                                    (task: UploadStatementTask): void => {
                                        const {
                                            taskId,
                                            canceled,
                                            error,
                                            paused,
                                            output,
                                        } = task;

                                        const statementView =
                                            output?.statementView ?? null;

                                        if (
                                            paused &&
                                            error !== null &&
                                            !canceled &&
                                            statementView === null
                                        ) {
                                            sendTaskEvent({
                                                type: 'CANCEL',
                                                value: {
                                                    taskId,
                                                },
                                            });
                                        }
                                    },
                                );
                            }}
                        >
                            <IconX width={20} fillColor="tertiary-400" />
                        </button>
                    </div>
                    <div className="flex justify-between">
                        <h3>Manage Statements</h3>
                        <Tooltip
                            addClasses="mt-2 respond-to-mouse text-black"
                            messageClasses="respond-to-mouse"
                            background="light"
                            maxWidth={600}
                            minWidth={600}
                            RenderMessage={PROStatementHelp}
                            position="bottom"
                        >
                            <span className="text-sm border-b-2 border-dashed text-primary-500 border-primary-500">
                                {
                                    locale.enUS.MANAGE_STATEMENTS
                                        .HOW_DO_I_GET_A_CSV
                                }
                            </span>
                        </Tooltip>
                    </div>
                    <div className="flex justify-between mt-6">
                        <div className="mr-10 text-sm">
                            {
                                locale.enUS.MANAGE_STATEMENTS
                                    .DRAG_AND_DROP_OR_BROWSE
                            }
                            <div className="flex items-center mt-3 text-xs">
                                <IconInfoCircle
                                    fillColor="tertiary-400"
                                    width={16}
                                    addClasses="mr-1"
                                />
                                <div className="text-xs italic text-tertiary-400">{`${locale.enUS.SUPPORTED_PROS} ${supportedProsLabel}.`}</div>
                                <Tooltip
                                    addClasses="respond-to-mouse text-black"
                                    messageClasses="respond-to-mouse"
                                    background="light"
                                    maxWidth={600}
                                    minWidth={600}
                                    RenderMessage={HowIngestionWorks}
                                    position="bottom"
                                >
                                    <div className="ml-2 text-xs italic border-b-2 border-dashed text-primary-500 border-primary-500">
                                        {
                                            locale.enUS.MANAGE_STATEMENTS
                                                .HOW_DO_UPLOADS_WORK
                                        }
                                    </div>
                                </Tooltip>
                            </div>
                        </div>
                        <Button
                            color="primary"
                            autoWidth={true}
                            addClasses="flex text-sm font-semibold items-center px-6 respond-to-mouse z-10"
                            boxShadow={true}
                            handleClick={addFiles}
                        >
                            <IconFile
                                fillColor="tertiary-200"
                                addClasses="mr-2"
                            />
                            {locale.enUS.MANAGE_STATEMENTS.UPLOAD_STATEMENTS}
                            <input
                                accept={fileInputAccept}
                                className="visually-hidden"
                                id="manage-statements-file-input"
                                data-cy="manage-statements-file-input"
                                onChange={(e: SyntheticEvent): void => {
                                    e.preventDefault();

                                    if (fileInputRef.current) {
                                        const files = Array.from(
                                            fileInputRef.current?.files ?? [],
                                        );

                                        uploadOrRejectFiles(files);

                                        // Reset the input's current value so it can be used again.
                                        fileInputRef.current.value = '';
                                    }
                                }}
                                ref={fileInputRef}
                                type="file"
                                multiple={true}
                            />
                        </Button>
                    </div>
                </div>
                <TabPanel
                    activeTabKey={activeTabKey}
                    setActiveTabKey={setActiveTabKey}
                    addClasses="px-10"
                    overflowHidden={false}
                    panels={[
                        /** Complete tab */
                        {
                            key: HASHES.MANAGE_STATEMENTS.COMPLETE,
                            label: (
                                <div className="flex items-center">
                                    {
                                        locale.enUS.MANAGE_STATEMENTS.TAB_LABELS
                                            .COMPLETE
                                    }
                                    {` (${completeCount})`}
                                    {unviewedStatements.length > 0 && (
                                        <div
                                            className="mx-1 rounded-full bg-secondary-500"
                                            style={{ width: 6, height: 6 }}
                                        />
                                    )}
                                </div>
                            ),
                            contents: (
                                <div
                                    className="relative bg-white rounded-b-lg"
                                    style={{
                                        height: tabPanelHeight,
                                    }}
                                >
                                    <div className="flex flex-col items-center justify-start h-full">
                                        {!loading && (
                                            <ManageStatementsTable
                                                height={
                                                    tableHeight -
                                                    (selectedStatementIds.length <
                                                    1
                                                        ? 0
                                                        : modalFooterHeight -
                                                          modalFooterShimHeight)
                                                }
                                                resetRowSelection={
                                                    resetRowSelection
                                                }
                                                rowData={activeEntities}
                                                selectedStatementIds={
                                                    selectedStatementIds
                                                }
                                                setSelectedStatementIds={
                                                    setSelectedStatementIds
                                                }
                                            />
                                        )}
                                        {loading && (
                                            <div className="flex flex-col justify-center h-full">
                                                <LoadingSpinner />
                                            </div>
                                        )}
                                    </div>
                                    {selectedStatementIds.length > 0 && (
                                        <div
                                            className="absolute z-10 flex justify-end w-full pb-4 bg-white rounded-b-lg shadow-2xl"
                                            style={{
                                                height: modalFooterHeight,
                                                bottom: -modalFooterShimHeight,
                                            }}
                                        >
                                            <Button
                                                color="danger-secondary-light-bg"
                                                autoWidth={true}
                                                addClasses="mr-12 mt-4 respond-to-mouse"
                                                handleClick={(): void => {
                                                    addToast({
                                                        kind: 'warning-card',
                                                        onRemove: (
                                                            input?: RemoveToastInput,
                                                        ): void => {
                                                            const success =
                                                                input?.success ??
                                                                false;
                                                            const canceled =
                                                                input?.canceled ??
                                                                false;

                                                            if (
                                                                !success &&
                                                                !canceled
                                                            ) {
                                                                addToast({
                                                                    kind: 'warning-banner',
                                                                    content:
                                                                        deleteStatementErrorMessage,
                                                                });
                                                            } else if (
                                                                success &&
                                                                !canceled
                                                            ) {
                                                                addToast({
                                                                    kind: 'info-banner',
                                                                    content:
                                                                        deleteStatementSuccessMessage,
                                                                });

                                                                setResetRowSelection(
                                                                    Date.now(),
                                                                );

                                                                setSelectedStatementIds(
                                                                    [],
                                                                );
                                                            }
                                                        },
                                                        RenderContent:
                                                            ConfirmStatementDeletion,
                                                        renderContentInput: {
                                                            selectedStatementIds,
                                                            userId,
                                                        },
                                                        persist: true,
                                                        position: 'center',
                                                    });
                                                }}
                                            >
                                                {`${locale.enUS.MANAGE_STATEMENTS.DELETE_SELECTED} (${selectedStatementIds.length})`}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ),
                        },
                        /** Uploads tab */
                        {
                            key: HASHES.MANAGE_STATEMENTS.UPLOADS,
                            label: (
                                <div>
                                    {
                                        locale.enUS.MANAGE_STATEMENTS.TAB_LABELS
                                            .UPLOADS
                                    }
                                    <span
                                        style={{ width: 20 }}
                                        className={cx('font-semibold ml-1', {
                                            'text-secondary-500':
                                                inProgressTasks.length > 0,
                                        })}
                                    >
                                        {`(${inProgressTasks.length})`}
                                    </span>
                                </div>
                            ),
                            contents: (
                                <ActiveIngestions
                                    height={tabPanelHeight}
                                    inProgressTasks={inProgressTasks}
                                    manageGapAnalysisHeaderData={
                                        manageGapAnalysisHeaderData
                                    }
                                    remove={props.remove}
                                />
                            ),
                        },
                        /** Alerts tab */
                        {
                            key: HASHES.MANAGE_STATEMENTS.ALERTS,
                            label: (
                                <div>
                                    {
                                        locale.enUS.MANAGE_STATEMENTS.TAB_LABELS
                                            .ALERTS
                                    }
                                    <span
                                        className={cx('font-semibold ml-1', {
                                            'text-quinary-500':
                                                failedIngestions.length > 0,
                                        })}
                                    >
                                        {`(${failedIngestions.length})`}
                                    </span>
                                </div>
                            ),
                            contents: (
                                <div
                                    className="relative bg-white rounded-b-lg"
                                    style={{
                                        height: tabPanelHeight,
                                    }}
                                >
                                    <div className="flex flex-col items-center justify-start h-full">
                                        <FailedIngestionsTable
                                            height={tableHeight}
                                            setSelectedRowIds={
                                                setSelectedFailedIngestionRowIds
                                            }
                                            failedIngestions={failedIngestions}
                                        />
                                    </div>
                                </div>
                            ),
                        },
                    ]}
                />
                <div style={{ height: modalFooterShimHeight }} />
            </div>
        </FileDropZone>
    );
};
